import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { ReactNode } from 'react';

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const badgeTones: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  info: 'bg-sky-50 text-sky-800 ring-sky-200',
  success: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
};

const dotTones: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  info: 'bg-sky-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
};

const alertTones: Record<Tone, string> = {
  neutral: 'border-slate-200 bg-white text-slate-800',
  info: 'border-sky-200 bg-sky-50 text-sky-900',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  danger: 'border-red-200 bg-red-50 text-red-800',
};

const alertIcons: Record<Tone, ReactNode> = {
  neutral: <Info aria-hidden="true" className="size-5 text-slate-500" />,
  info: <Info aria-hidden="true" className="size-5 text-sky-600" />,
  success: <CheckCircle2 aria-hidden="true" className="size-5 text-emerald-600" />,
  warning: <AlertTriangle aria-hidden="true" className="size-5 text-amber-600" />,
  danger: <AlertCircle aria-hidden="true" className="size-5 text-red-600" />,
};

interface BadgeProps {
  readonly tone?: Tone;
  readonly dot?: boolean;
  readonly children: ReactNode;
}

export function Badge({ tone = 'neutral', dot = false, children }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ring-1 ring-inset ${badgeTones[tone]}`}
    >
      {dot ? (
        <span aria-hidden="true" className={`size-1.5 rounded-full ${dotTones[tone]}`} />
      ) : null}
      {children}
    </span>
  );
}

interface AlertProps {
  readonly tone?: Tone;
  readonly title?: string;
  readonly children?: ReactNode;
}

export function Alert({ tone = 'info', title, children }: AlertProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={`flex gap-3 rounded-xl border px-4 py-3 text-sm ${alertTones[tone]}`}
    >
      <span className="mt-px shrink-0">{alertIcons[tone]}</span>
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={title ? 'mt-1' : ''}>{children}</div> : null}
      </div>
    </div>
  );
}

interface EmptyStateProps {
  readonly title: string;
  readonly icon?: ReactNode;
  readonly action?: ReactNode;
  readonly children?: ReactNode;
}

export function EmptyState({ title, icon, action, children }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
      {icon ? (
        <span className="mb-3 flex size-11 items-center justify-center rounded-full bg-slate-100 text-slate-500 [&_svg]:size-5">
          {icon}
        </span>
      ) : null}
      <p className="font-semibold text-slate-900">{title}</p>
      {children ? <div className="mt-1.5 max-w-md text-sm text-slate-600">{children}</div> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}
